// src/client/renderer/vfx/env/env-ceremony-manager.ts
//
// Plan 18, Task 1.1 — EnvCeremonyVfxManager skeleton.
// Plan 18, Task 6.1 — APPENDED: createEnvCeremonyVfxPools + createEnvCeremonyEventResolver
//   bundle factories (APPEND-ONLY — do NOT edit the existing createEnvCeremonyVfxManager body).
//
// Design mirrors tile-vfx-manager.ts and item-vfx-manager.ts:
//   - per-pool active-set Map keyed by poolId
//   - closure-stored subscriber ids (TWO buses: eventLog + ceremonyBus)
//   - idempotent disposed flag guard
//   - pool.update(dt) → ReadonlySet iteration per tick
//
// Extension beyond tile/item managers:
//   Subscribes to TWO buses (eventLog + ceremonyBus) instead of one.
//   Stores both subscriber ids in closure; unsubscribes both on dispose.
//   Fires audio.onAudioCue(CEREMONY_AUDIO_CUES[event.type]) BEFORE pool.acquire
//   on ceremony-event dispatch (so cue lands at start-of-effect).
//
// ── Task 6.1 bundle factory design notes ──────────────────────────────────────
//
// AwardRevealedEvent fan-out (two pools: awards_spotlight + confetti):
//   Strategy: Option 1 — TWO resolvers, TWO ceremony-bus subscriptions.
//   The wire-up in build-board.ts creates TWO managers: one with the default
//   resolver (awards_spotlight priority) and one with resolveForConfetti=true
//   (confetti priority). Both managers subscribe to the same ceremonyBus.
//   On an award_revealed event, both subscriptions fire independently, each
//   manager.spawn()-ing its own pool. This is simpler than wrapping fan-out
//   logic inside a single resolver and avoids needing a special multi-return type.
//   See createEnvCeremonyEventResolver for the resolveForConfetti option.
//
// Ceremony resolver priority order (default resolver):
//   1. award_revealed → awards_spotlight
//   2. trophy_shine   → trophy_shine
//   3. game_over      → fireworks
//   4. awards_ceremony_start → null (no-op; phase signal only)
//
// Ceremony resolver priority order (resolveForConfetti=true):
//   1. award_revealed → confetti
//   All other events → null

import * as THREE from 'three';
import type {
  EnvCeremonyPoolId,
  EnvCeremonyPool,
  EnvCeremonySpawnArgs,
  EnvCeremonyVfxManager,
  EnvCeremonyVfxManagerConfig,
  CeremonyEventResolver,
} from './env-ceremony-types';
import type { GameEvent } from '../../../../shared/game/types';
import type { CeremonyEvent } from './ceremony-events';
import { CEREMONY_AUDIO_CUES } from './ceremony-events';
import { NO_OP_AUDIO_HOOKS } from '../audio-hooks';
// Task 6.1 bundle factory imports (pool factories + per-pool resolvers)
import { createShootingStarsPool } from './shooting-stars-pool';
import { createDustMotesPool } from './dust-motes-pool';
import { createLanternFlickerPool } from './lantern-flicker-pool';
import { createCrystalPulsePool } from './crystal-pulse-pool';
import { createWaterfallMistPool } from './waterfall-mist-pool';
import { createBirdsPool } from './birds-pool';
import { createAwardsSpotlightPool, resolveAwardsSpotlightForEvent } from './awards-spotlight-pool';
import { createConfettiPool, resolveConfettiForEvent } from './confetti-pool';
import { createTrophyShinePool, resolveTrophyShineForEvent } from './trophy-shine-pool';
import { createFireworksPool, resolveFireworksForEvent } from './fireworks-pool';
import type { GsapRegistry } from '../gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// createEnvCeremonyVfxManager
// ─────────────────────────────────────────────────────────────────────────────

export function createEnvCeremonyVfxManager(
  config: EnvCeremonyVfxManagerConfig,
): EnvCeremonyVfxManager {
  const { scene, eventLog, ceremonyBus, pools, ceremonyResolver } = config;
  const audio = config.audio ?? NO_OP_AUDIO_HOOKS;

  // Per-pool active emitter tracking.
  // Manager-side set tracks emitters that have been added to the scene;
  // pool.update(dt) returns the authoritative still-active set from the pool's
  // perspective, which we use on each tick to discover newly-finished emitters.
  const activeByPool = new Map<EnvCeremonyPoolId, Set<THREE.Object3D>>();
  for (const [poolId] of pools) {
    activeByPool.set(poolId, new Set<THREE.Object3D>());
  }

  let disposed = false;

  // ── Event subscribers ──────────────────────────────────────────────────────

  // eventLog subscriber (ambient pools may gate on game lifecycle events).
  // Currently this is a no-op gate handler — ambient pools that need to react
  // to game events will register their own internal handlers via the resolver
  // pattern in later tasks. For Task 1.1 the eventLog subscription is declared
  // here so the subscriber id is stored in closure and unsubscribed on dispose.
  function handleGameEvent(_event: GameEvent): void {
    // Reserved for ambient pool game-event gating in future tasks.
    // The env+ceremony manager does not resolve GameEvents to pool spawns —
    // that path is exclusively through the ceremonyBus → ceremonyResolver chain.
  }

  // ceremonyBus subscriber — drives ceremony pool spawning.
  function handleCeremonyEvent(event: CeremonyEvent): void {
    const result = ceremonyResolver(event);
    if (result === null) return;

    // Fire audio cue BEFORE pool.acquire (per spec: cue lands at start-of-effect).
    audio.onAudioCue(CEREMONY_AUDIO_CUES[event.type]);

    spawn(result.poolId, result.args);
  }

  const eventLogSubscriberId = eventLog.subscribe(handleGameEvent);
  const ceremonyBusSubscriberId = ceremonyBus.subscribe(handleCeremonyEvent);

  // ── spawn ──────────────────────────────────────────────────────────────────

  function spawn(poolId: EnvCeremonyPoolId, args: EnvCeremonySpawnArgs): void {
    const pool = pools.get(poolId);
    if (!pool) return; // unknown poolId — no-op

    const emitter = pool.acquire(args);
    if (emitter === null) return; // pool full — no-op

    scene.add(emitter);

    let activeSet = activeByPool.get(poolId);
    if (!activeSet) {
      // Defensive: pool was added after construction (shouldn't normally happen)
      activeSet = new Set<THREE.Object3D>();
      activeByPool.set(poolId, activeSet);
    }
    activeSet.add(emitter);
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): void {
    for (const [poolId, activeSet] of activeByPool) {
      const pool = pools.get(poolId);
      if (!pool) continue;

      // Per-pool update — returns the set of emitters STILL active after this tick.
      // This is the authoritative list; we do NOT call pool.update(emitter, dt) per-emitter.
      const stillActive = pool.update(deltaSeconds);

      // Walk the pool's returned active set to detect newly-finished emitters.
      const toRemove: THREE.Object3D[] = [];
      for (const emitter of stillActive) {
        if (pool.isFinished(emitter)) {
          toRemove.push(emitter);
        }
      }
      // Also handle emitters in manager set that pool no longer reports at all
      // (pool already recycled them without going through isFinished=true path).
      for (const emitter of activeSet) {
        if (!stillActive.has(emitter) && !toRemove.includes(emitter)) {
          toRemove.push(emitter);
        }
      }

      for (const emitter of toRemove) {
        if (activeSet.has(emitter)) {
          scene.remove(emitter);
          pool.release(emitter);
          activeSet.delete(emitter);
        }
      }

      // Sync manager set to match what the pool says is still running
      // (add any emitters the pool reports but manager doesn't yet know about —
      //  this handles pools that track emitters internally without manager mediation)
      for (const emitter of stillActive) {
        if (!pool.isFinished(emitter) && !activeSet.has(emitter)) {
          activeSet.add(emitter);
        }
      }
    }
  }

  // ── activeCount ────────────────────────────────────────────────────────────

  function getActiveCount(): number {
    let total = 0;
    for (const activeSet of activeByPool.values()) {
      total += activeSet.size;
    }
    return total;
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return; // idempotent guard
    disposed = true;

    // Unsubscribe from BOTH buses (TWO subscriber ids stored in closure)
    eventLog.unsubscribe(eventLogSubscriberId);
    ceremonyBus.unsubscribe(ceremonyBusSubscriberId);

    // Call pool.dispose() on every pool (final GPU teardown) and clear active sets
    for (const [poolId, activeSet] of activeByPool) {
      const pool = pools.get(poolId);
      if (pool) {
        // Remove all live emitters from the scene before pool disposal
        for (const emitter of activeSet) {
          scene.remove(emitter);
        }
        pool.dispose();
      }
      activeSet.clear();
    }
  }

  return {
    spawn,
    update,
    get activeCount() {
      return getActiveCount();
    },
    dispose,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Plan 18, Task 6.1 — Bundle factories (APPENDED — do NOT edit above)
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// createEnvCeremonyVfxPools — bundle factory for env+ceremony pools
// ─────────────────────────────────────────────────────────────────────────────
//
// Returns a frozen ReadonlyMap<EnvCeremonyPoolId, EnvCeremonyPool> with all
// pools instantiated. Each pool receives only the deps it needs.
//
// Optional callbacks (getLanterns, getCrystals, getFountainAnchors, getTrophyMesh)
// are passed through to their respective pools. Missing callbacks cause those pools
// to be graceful no-ops (acquire returns null) — per the individual pool specs.
//
// GsapRegistry tick contract (HF1 parity with item-VFX):
//   Each pool's update() DOES NOT advance the registry. Only the caller
//   (via build-board.ts envCeremonyRegistry.update(dt)) advances the registry
//   once per frame across all pools. Prevents N× tick-rate multiplication.

export interface CreateEnvCeremonyVfxPoolsDeps {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
  readonly getLanterns?: () => readonly THREE.Object3D[];
  readonly getCrystals?: () => readonly THREE.Object3D[];
  readonly getFountainAnchors?: () => readonly THREE.Vector3[];
  readonly getTrophyMesh?: () => THREE.Object3D | undefined;
}

/**
 * Bundle factory: returns env+ceremony pools instantiated with the shared
 * scene + GsapRegistry. Returned Map is frozen.
 *
 * Call site:
 *   const registry = createGsapRegistry();
 *   const pools = createEnvCeremonyVfxPools({ scene, registry });
 *   const resolver = createEnvCeremonyEventResolver({});
 *   const manager = createEnvCeremonyVfxManager({ scene, eventLog, ceremonyBus, pools, ceremonyResolver: resolver });
 */
export function createEnvCeremonyVfxPools(
  deps: CreateEnvCeremonyVfxPoolsDeps,
): ReadonlyMap<EnvCeremonyPoolId, EnvCeremonyPool> {
  const { scene, registry, getLanterns, getCrystals, getFountainAnchors, getTrophyMesh } = deps;

  const pools = new Map<EnvCeremonyPoolId, EnvCeremonyPool>([
    // ── Ambient pools (sky + atmosphere) ────────────────────────────────────
    ['shooting_stars', createShootingStarsPool({ scene, registry })],
    ['dust_motes', createDustMotesPool({ scene })],

    // ── Decoration-mesh ambient pools (decoration callbacks optional) ────────
    ['lantern_flickers', createLanternFlickerPool({ getLanterns })],
    ['crystal_pulses', createCrystalPulsePool({ getCrystals })],

    // ── Sporadic ambient pool (fountain callback optional) ───────────────────
    ['waterfall_mist', createWaterfallMistPool({ scene, registry, getFountainAnchors })],

    // ── Trajectory ambient pool (birds) ─────────────────────────────────────
    ['birds', createBirdsPool({ scene, registry })],

    // ── Ceremony pools ───────────────────────────────────────────────────────
    ['awards_spotlight', createAwardsSpotlightPool({ scene, registry })],
    ['confetti', createConfettiPool({ scene, registry })],
    ['trophy_shine', createTrophyShinePool({ scene, registry, getTrophyMesh })],
    ['fireworks', createFireworksPool({ scene, registry })],
  ]);

  return Object.freeze(pools);
}

// ─────────────────────────────────────────────────────────────────────────────
// createEnvCeremonyEventResolver — composed ceremony-event resolver
// ─────────────────────────────────────────────────────────────────────────────
//
// Resolution strategy: Option 1 (two resolver calls, two bus subscriptions).
// The wire-up in build-board.ts subscribes the ceremonyBus TWICE — once with
// the default resolver (resolves award_revealed → awards_spotlight) and once
// with resolveForConfetti=true (resolves award_revealed → confetti). Both
// subscriptions fire independently on a single event, enabling the fan-out
// without a special multi-return resolver type.
//
// Default resolver priority order:
//   1. award_revealed       → awards_spotlight
//   2. trophy_shine         → trophy_shine
//   3. game_over            → fireworks
//   4. awards_ceremony_start → null  (no-op — phase signal, no visual)
//
// Confetti resolver (resolveForConfetti=true):
//   1. award_revealed → confetti
//   2. all others    → null

export interface CreateEnvCeremonyEventResolverOpts {
  /**
   * When true, the returned resolver acts as the confetti fan-out resolver:
   *   award_revealed → confetti (all other events → null).
   * When false/absent (default), the resolver handles awards_spotlight,
   *   trophy_shine, fireworks, and returns null for awards_ceremony_start.
   */
  readonly resolveForConfetti?: boolean;
}

/**
 * Bundle factory: returns a composed CeremonyEventResolver that routes ceremony
 * events to the appropriate pool.
 *
 * Use resolveForConfetti=true to get the confetti fan-out resolver
 * (award_revealed → confetti). The default resolver handles all other events.
 * The wire-up site subscribes to the ceremonyBus with BOTH resolvers to achieve
 * the dual fan-out on award_revealed events.
 */
export function createEnvCeremonyEventResolver(
  opts: CreateEnvCeremonyEventResolverOpts,
): CeremonyEventResolver {
  const { resolveForConfetti = false } = opts;

  return function resolveEnvCeremonyEvent(event: import('./ceremony-events').CeremonyEvent): {
    poolId: EnvCeremonyPoolId;
    args: import('./env-ceremony-types').EnvCeremonySpawnArgs;
  } | null {
    if (resolveForConfetti) {
      // Confetti fan-out resolver: only handles award_revealed → confetti
      return resolveConfettiForEvent(event);
    }

    // ── Default resolver ────────────────────────────────────────────────────

    // 1. award_revealed → awards_spotlight (highest ceremony visual priority)
    const spotlight = resolveAwardsSpotlightForEvent(event);
    if (spotlight !== null) return spotlight;

    // 2. trophy_shine → trophy_shine
    const trophyShine = resolveTrophyShineForEvent(event);
    if (trophyShine !== null) return trophyShine;

    // 3. game_over → fireworks
    const fireworks = resolveFireworksForEvent(event);
    if (fireworks !== null) return fireworks;

    // 4. awards_ceremony_start → null (no-op; phase signal only)
    return null;
  };
}
